export const AUTOMATION_SIDEBAR_TAB_ID = 'streamlain-automation';
export const OPEN_WORKFLOW_AUTOMATION_USAGE_EVENT =
  'streamlain:open-workflow-automation-usage';
export const PENDING_WORKFLOW_AUTOMATION_USAGE_KEY =
  'streamlain.pending-workflow-automation-usage.v1';

const PENDING_WORKFLOW_AUTOMATION_USAGE_MAX_AGE_MS = 60_000;

export interface OpenWorkflowAutomationUsageDetail {
  docId: string;
  blockId: string;
  _routed?: boolean;
  createdAt?: number;
}

export function shouldSyncAutomationCanvasSelection({
  activeSidebarTabId,
  sidebarOpen,
}: {
  activeSidebarTabId: string | null;
  sidebarOpen: boolean;
}): boolean {
  return sidebarOpen && activeSidebarTabId === AUTOMATION_SIDEBAR_TAB_ID;
}

function getBrowserSessionStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function normalizePendingWorkflowAutomationUsageTarget(
  value: unknown
): OpenWorkflowAutomationUsageDetail | null {
  if (!value || typeof value !== 'object') return null;
  const detail = value as Partial<OpenWorkflowAutomationUsageDetail>;
  if (typeof detail.docId !== 'string' || !detail.docId) return null;
  if (typeof detail.blockId !== 'string' || !detail.blockId) return null;
  const createdAt =
    typeof detail.createdAt === 'number' ? detail.createdAt : Date.now();

  return {
    docId: detail.docId,
    blockId: detail.blockId,
    createdAt,
  };
}

export function storePendingWorkflowAutomationUsageTarget(
  detail: OpenWorkflowAutomationUsageDetail
): void {
  const storage = getBrowserSessionStorage();
  if (!storage) return;

  try {
    storage.setItem(
      PENDING_WORKFLOW_AUTOMATION_USAGE_KEY,
      JSON.stringify({
        docId: detail.docId,
        blockId: detail.blockId,
        createdAt: Date.now(),
      })
    );
  } catch {
    // Navigation still works without sessionStorage; event retries cover
    // already-mounted editors.
  }
}

export function takePendingWorkflowAutomationUsageTarget(
  docId: string
): OpenWorkflowAutomationUsageDetail | null {
  const storage = getBrowserSessionStorage();
  if (!storage) return null;

  let raw: string | null = null;
  try {
    raw = storage.getItem(PENDING_WORKFLOW_AUTOMATION_USAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    storage.removeItem(PENDING_WORKFLOW_AUTOMATION_USAGE_KEY);
    return null;
  }

  const detail = normalizePendingWorkflowAutomationUsageTarget(parsed);
  if (!detail) {
    storage.removeItem(PENDING_WORKFLOW_AUTOMATION_USAGE_KEY);
    return null;
  }

  if (
    Date.now() - (detail.createdAt ?? 0) >
    PENDING_WORKFLOW_AUTOMATION_USAGE_MAX_AGE_MS
  ) {
    storage.removeItem(PENDING_WORKFLOW_AUTOMATION_USAGE_KEY);
    return null;
  }

  if (detail.docId !== docId) return null;

  storage.removeItem(PENDING_WORKFLOW_AUTOMATION_USAGE_KEY);
  return detail;
}

export function dispatchOpenWorkflowAutomationUsage(
  detail: OpenWorkflowAutomationUsageDetail
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(OPEN_WORKFLOW_AUTOMATION_USAGE_EVENT, { detail })
  );
}
